import { useMemo } from 'react'
import { BigNumber } from 'bignumber.js'
import { useUserFarmLPInfo, usePoolInfo } from 'hooks/craftsman/useCraftsmanContract'
import { useGetFarmBoostPoolInfo, useGetPotentialAdjustShares } from 'hooks/boost-farm/useFarmBoostContract'
import { useMyMoleIdsWithStaked } from 'views/VvsMinerMoleMyMoles/hooks/useMyMoles'
import { getVvsMinerMoleAddress } from 'utils/addressHelpers'
import { useFarmTotalSize } from 'hooks/boost-farm/useFarmTotalSize'
import getBoostFactor from 'utils/getBoostFactor'
import { DEFAULT_TOKEN_DECIMAL } from 'config'
import { useBoostTokenBalanceOfUser } from 'hooks/vvs-boost/useVvsBoostContract'
import farms from 'config/constants/farms'

export interface BoostFactorParams {
  farmId: number
  boostBalanceAmount?: number
  extraFarmPosition?: number
  nfts?: string[] // tokenIds
}

const useBoostFactor = (boostParams: BoostFactorParams, shouldGetActualAdjustShare = false) => {
  const { farmId, boostBalanceAmount = 0, extraFarmPosition = 0, nfts = [] } = boostParams
  const targetFarm = useMemo(() => farms.find((farm) => farm.pid === farmId), [farmId])
  const isFarmBoost = targetFarm.enableBoost

  // userFarmPosition = userFarmInfo[0]
  const { data: userFarmInfo, isLoading: isUserFarmInfoLoading } = useUserFarmLPInfo(targetFarm, {
    enabled: isFarmBoost,
  })
  // baseAllocation = poolInfo[1]
  const { data: poolInfo, isLoading: isPoolInfoLoading } = usePoolInfo(farmId, {
    enabled: isFarmBoost,
  })
  // potentialAdjustSharesInfo = useVVSBoostPoolInfo(vaultId)
  const { data: userExistingBoostTokenBalanceInfo, isLoading: isUserExistingBoostTokenBalanceLoading } =
    useBoostTokenBalanceOfUser({
      enabled: isFarmBoost,
    })

  const { data: myMoleIdsWithStakedData, isLoading: isMyMoleLoading } = useMyMoleIdsWithStaked({
    enabled: isFarmBoost,
  })
  const farmStakedMoleIds = myMoleIdsWithStakedData?.boostedFarmUserInfosMap ?? {}

  const nftIds = [
    ...nfts,
    ...(shouldGetActualAdjustShare ? farmStakedMoleIds?.[farmId]?.[2]?.map((nft) => nft[1].toString()) ?? [] : []),
  ]

  const extraFarmPositionString = new BigNumber(extraFarmPosition).times(DEFAULT_TOKEN_DECIMAL).toString()
  const { data: potentialAdjustSharesInfo, isLoading: isAdjustSharesInfoLoading } = useGetPotentialAdjustShares(
    farmId,
    new BigNumber(boostBalanceAmount.toString())
      .times(DEFAULT_TOKEN_DECIMAL)
      .plus(userExistingBoostTokenBalanceInfo ? userExistingBoostTokenBalanceInfo.toString() : 0),
    new BigNumber(extraFarmPositionString).plus(userFarmInfo ? userFarmInfo[0].toString() : 0),
    nftIds.map((tokenId) => ({
      tokenContract: getVvsMinerMoleAddress(),
      tokenId,
    })),
    {
      enabled: isFarmBoost,
    },
  )

  // depositTokenPid = farmBoostInfo[1] ;
  // boostAllocation = boostAllocationPoolInfo[1]
  const { data: farmBoostInfo, isLoading: isFarmBoostInfoLoading } = useGetFarmBoostPoolInfo(farmId, {
    enabled: isFarmBoost,
  })
  const { data: boostAllocationPoolInfo, isLoading: isBoostAllocationPoolInfoLoading } = usePoolInfo(
    farmBoostInfo && farmBoostInfo[1],
    {
      enabled: isFarmBoost,
    },
  )

  const { data: farmTotalSizeData, isLoading: isFarmTotalSizeLoading } = useFarmTotalSize(targetFarm)

  const data = useMemo(() => {
    if (
      isUserFarmInfoLoading ||
      isPoolInfoLoading ||
      isAdjustSharesInfoLoading ||
      isFarmBoostInfoLoading ||
      isBoostAllocationPoolInfoLoading ||
      isFarmTotalSizeLoading ||
      isUserExistingBoostTokenBalanceLoading ||
      isMyMoleLoading
    ) {
      return {
        baseAllocation: undefined,
        boostAllocation: undefined,
        adjustShares: undefined,
        boostFactor: undefined,
      }
    }

    const userFarmPosition = (userFarmInfo && new BigNumber(userFarmInfo[0].toString())) || new BigNumber(0)
    const baseAllocation = (poolInfo && new BigNumber(poolInfo[1].toString())) || new BigNumber(0)
    const boostAllocation =
      (boostAllocationPoolInfo && new BigNumber(boostAllocationPoolInfo[1].toString())) || new BigNumber(0)
    const farmTotalSize = farmTotalSizeData || new BigNumber(0)
    const adjustShares = potentialAdjustSharesInfo || new BigNumber(0)

    return {
      baseAllocation,
      boostAllocation,
      adjustShares: adjustShares.div(DEFAULT_TOKEN_DECIMAL),
      boostFactor: getBoostFactor(
        userFarmPosition.plus(extraFarmPositionString),
        baseAllocation,
        boostAllocation,
        farmTotalSize.plus(extraFarmPositionString),
        adjustShares,
      ),
    }
  }, [
    isUserFarmInfoLoading,
    isPoolInfoLoading,
    isAdjustSharesInfoLoading,
    isFarmBoostInfoLoading,
    isBoostAllocationPoolInfoLoading,
    isFarmTotalSizeLoading,
    isUserExistingBoostTokenBalanceLoading,
    isMyMoleLoading,
    userFarmInfo,
    poolInfo,
    boostAllocationPoolInfo,
    farmTotalSizeData,
    potentialAdjustSharesInfo,
    extraFarmPositionString,
  ])

  return {
    data,
    isLoading:
      isUserFarmInfoLoading ||
      isPoolInfoLoading ||
      isAdjustSharesInfoLoading ||
      isFarmBoostInfoLoading ||
      isBoostAllocationPoolInfoLoading ||
      isFarmTotalSizeLoading ||
      isUserExistingBoostTokenBalanceLoading ||
      isMyMoleLoading,
  }
}

export default useBoostFactor
